import { Link, NavLink, Outlet } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, CandlestickChart, BrainCircuit, ListOrdered, FlaskConical, BarChart3, Newspaper, Landmark, ShieldAlert, KeyRound, Users, LogOut, Menu, X, Globe2, Radar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/components/Common";

const NAV = [
  { to: "/command", label: "NEXUS Command", icon: BrainCircuit, perm: "manage_users" },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "view_dashboard" },
  { to: "/markets", label: "Markets", icon: CandlestickChart, perm: "view_markets" },
  { to: "/desks", label: "Decision Desks", icon: BrainCircuit, perm: "run_analysis" },
  { to: "/trades", label: "Trades & Journal", icon: ListOrdered, perm: "view_dashboard" },
  { to: "/backtest", label: "Backtesting", icon: FlaskConical, perm: "backtest" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, perm: "view_analytics" },
  { to: "/news", label: "News & Calendar", icon: Newspaper, perm: "view_markets" },
  { to: "/intelligence", label: "Global Intelligence", icon: Globe2, perm: "view_markets" },
  { to: "/sources", label: "Source Manager", icon: Radar, perm: "view_markets" },
  { to: "/broker", label: "Broker / MT5", icon: Landmark, perm: "manage_brokers" },
  { to: "/risk", label: "Engine & Risk", icon: ShieldAlert, perm: "view_dashboard" },
  { to: "/settings", label: "Credentials", icon: KeyRound, perm: "manage_credentials" },
  { to: "/users", label: "Users", icon: Users, perm: "manage_users" },
];

function Sidebar({ onNav }) {
  const { can } = useAuth();
  return (
    <nav className="flex flex-col gap-1 p-3">
      {NAV.filter((n) => can(n.perm)).map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          onClick={onNav}
          data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors duration-150 ${isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`
          }
        >
          <Icon size={16} /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { data: risk } = useFetch("/risk", [], 15000);
  const { data: eng } = useFetch("/engine", [], 15000);
  const stopped = risk?.config?.emergency_stop;
  const running = eng?.config?.enabled;

  return (
    <div className="min-h-screen bg-background bg-grid">
      <header className="sticky top-0 z-50 flex h-14 items-center justify-between gap-3 border-b border-border/80 bg-[hsl(224_25%_6%/0.85)] px-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X size={20} /> : <Menu size={20} />}</button>
          <span className="font-display text-xl font-extrabold uppercase tracking-tight">NEXUS <span className="text-gold">Trade Command</span></span>
          <Link to="/risk" data-testid="engine-status-badge" className="hidden items-center gap-2 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors hover:border-primary/60 hover:text-primary sm:flex">
            <span aria-hidden className={`h-2 w-2 rounded-full ${stopped ? "bg-[hsl(var(--down))]" : running ? "bg-[hsl(var(--up))] pulse-dot" : "bg-muted-foreground"}`} />
            {stopped ? "Halted" : running ? "Engine live" : "Engine idle"}
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-muted-foreground md:block" data-testid="current-user-email">{user.email} · {user.role}</span>
          <button data-testid="logout-button" onClick={logout} className="rounded p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"><LogOut size={16} /></button>
        </div>
      </header>
      {stopped && <div data-testid="emergency-banner" className="border-b border-[hsl(var(--down)/0.4)] bg-[hsl(var(--down)/0.12)] px-4 py-2 text-center font-mono text-xs uppercase tracking-widest text-down">Emergency Stop active — all automated and manual execution is blocked</div>}
      <div className="flex">
        <aside className={`${open ? "block" : "hidden"} fixed inset-y-14 left-0 z-40 w-60 border-r bg-card lg:sticky lg:top-14 lg:block lg:h-[calc(100vh-3.5rem)]`}>
          <Sidebar onNav={() => setOpen(false)} />
        </aside>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
